import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { DebouncedSearchInput, Spinner, Badge, Button } from '../components/common';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { useClient } from '../hooks/useClients';
import { masterDataService } from '../services/masterDataService';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import StorageSummaryModal from '../components/inventory/StorageSummaryModal';
import { InventoryItem, StorageSummaryFilters } from '../types';
import { formatDateTime } from '../utils/dateTime';
import { inventoryService } from '../services/inventoryService';

export default function ClientInventoryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = parseInt(id || '0');

  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>();
  const limit = 25;

  const [storageSummaryFilters, setStorageSummaryFilters] = useState<StorageSummaryFilters | null>(null);

  const { data: client, isLoading: clientLoading } = useClient(clientId.toString());

  const {
    data: inventoryData,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['inventory', 'client', clientId, { search, categoryFilter }],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await inventoryService.getClientInventory(clientId, {
        page: pageParam,
        limit,
        search: search || undefined,
        productCategoryId: categoryFilter,
      });
      return { items: response.data || [], total: response.total || 0, page: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, p) => sum + p.items.length, 0);
      return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!clientId && clientId > 0,
    staleTime: 0,
  });

  const inventory: InventoryItem[] = useMemo(() => {
    if (!inventoryData) return [];
    return inventoryData.pages.flatMap((p) => p.items || []);
  }, [inventoryData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { data: categoriesData } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => masterDataService.getProductCategories({ limit: 100, status: 'active' }),
  });

  const handleViewStorage = (item: InventoryItem) => {
    setStorageSummaryFilters({
      clientId: item.clientId,
      productDescription: item.productDescription,
      batchNo: item.batchNo,
    });
  };

  const inventoryColumns = [
    {
      key: 'productDescription',
      label: 'Product',
      width: '300px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-900 truncate" title={value}>
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'productCategoryName',
      label: 'Category',
      width: '150px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <Badge variant="info" rounded>
            {value}
          </Badge>
        </div>
      ),
    },
    {
      key: 'batchNo',
      label: 'Batch No',
      width: '120px',
      render: (value: string) => (
        <div className="flex justify-center items-center px-2">
          <span className="text-sm text-gray-700 truncate" title={value || 'N/A'}>
            {value || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'goodQty',
      label: 'Good Qty',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-green-700 bg-green-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'cdamQty',
      label: 'CDAM Qty',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-yellow-700 bg-yellow-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'damQty',
      label: 'DAM Qty',
      width: '100px',
      render: (value: number) => (
        <div className="flex justify-center items-center px-2">
          <span className="px-3 py-1 text-sm font-semibold text-red-700 bg-red-50 rounded-lg">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: 'total',
      label: 'Total',
      width: '100px',
      render: (_: any, row: InventoryItem) => {
        const total = row.goodQty + row.cdamQty + row.damQty;
        return (
          <div className="flex justify-center items-center px-2">
            <span className="px-3 py-1 text-sm font-bold text-gray-900 bg-gray-100 rounded-lg">
              {total}
            </span>
          </div>
        );
      },
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      width: '180px',
      render: (value: string) => (
        <div className="flex justify-center items-center text-sm text-gray-500 px-2">
          {formatDateTime(value)}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (_: any, row: InventoryItem) => (
        <div className="flex justify-center items-center px-2">
          <button
            onClick={() => handleViewStorage(row)}
            className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 inline-flex items-center transition-colors"
            title="View Storage"
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Storage
          </button>
        </div>
      ),
    },
  ];

  if (clientLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-red-800">Client not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen">
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="secondary" onClick={() => navigate('/clients')}>
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Clients
            </Button>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{client.companyName}</h1>
                <p className="text-gray-600 mt-1">Client Inventory</p>
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Contact:</span>{' '}
                    <span className="text-gray-900">{client.contactPersonName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="text-gray-900">{client.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>{' '}
                    <span className="text-gray-900">{client.phone}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>{' '}
                    <Badge variant="info" rounded>
                      {client.clientType ? client.clientType.toUpperCase() : 'N/A'}
                    </Badge>
                  </div>
                </div>
              </div>
              <Badge variant={client.status === 'active' ? 'success' : 'warning'} rounded>
                {client.status}
              </Badge>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              {searchOpen ? (
                <DebouncedSearchInput
                  placeholder="Search product, batch..."
                  onSearch={setSearch}
                  debounceMs={400}
                  onClose={() => { setSearchOpen(false); setSearch(''); }}
                />
              ) : (
                <button
                  onClick={() => setSearchOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-all text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </button>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <ResponsiveSelect
                value={categoryFilter || ''}
                onChange={(val) => {
                  setCategoryFilter(val ? parseInt(val) : undefined);
                }}
                placeholder="All Categories"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                options={(categoriesData?.data?.items ?? []).map((category: any) => ({ value: category.id, label: category.name }))}
              />
            </div>
          </div>
        </div>

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <p className="text-red-800">{(error as any)?.message || 'Failed to load inventory'}</p>
          </div>
        )}

        {!isError && (
          <ResponsiveTable
            columns={inventoryColumns}
            data={inventory}
            isLoading={isLoading || isFetchingNextPage}
            onLoadMore={handleLoadMore}
            hasMore={!!hasNextPage}
            height="calc(100vh - 280px)"
            emptyMessage="No inventory items found for this client"
          />
        )}

        <StorageSummaryModal
          isOpen={!!storageSummaryFilters}
          onClose={() => setStorageSummaryFilters(null)}
          filters={storageSummaryFilters}
        />
      </div>
    </Layout>
  );
}
